import { useCallback, useEffect, useState } from 'react'
import { get } from './lib/api'
import { me, type User } from './lib/auth'
import { t } from './lib/i18n'
import Shell from './components/Shell'
import ErrorBoundary from './components/ErrorBoundary'

export interface Health {
  app: string
  environment: 'local' | 'beta' | 'production'
  version: string
  commit: string
}

/**
 * The boot.
 *
 * Everything App used to do — the header, the tabs, the composition of
 * `UpdateBar`/`EnvBanner`/`InstallPrompt`/`DebugPanel` — now lives in `Shell`,
 * because a `useState<Tab>` cannot answer a notification deep link and the
 * router had to own the switch. What is left here is the one thing that has to
 * happen before any of it renders: who is this, and what is this.
 */
export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [health, setHealth] = useState<Health | null>(null)
  const [ready, setReady] = useState(false)

  const refresh = useCallback(async () => {
    setUser(await me())
  }, [])

  useEffect(() => {
    void (async () => {
      // Both in parallel: neither depends on the other, and the boot is the one
      // moment the user is definitely waiting.
      const [u, h] = await Promise.all([
        me(),
        get<Health>('/api/health').catch(() => null),
      ])
      setUser(u)
      setHealth(h)
      setReady(true)
    })()
  }, [])

  if (!ready) return <div className="boot">{t('app.loading')}</div>

  return (
    <ErrorBoundary>
      <Shell user={user} health={health} onAuthChange={refresh} />
    </ErrorBoundary>
  )
}
